import { HeaderComponent } from 'components/header/component'
import { RasaContext } from 'context'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import React from 'react'
import Select from 'react-select'
import { Dataset } from '../../generic/dataset'
import { Loading } from '../loading'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import * as GenericRedux from '../../generic/genericRedux'
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap'
import * as Flash from 'components/flash'
import './_styles.scss'

interface BillingSettingsState {
  communityId: string
  isLoading: boolean
  isPartnersLoading: boolean
  isPlansLoading: boolean
  isSaving: boolean
  showConfirmModal: boolean
  partners: any[]
  selectedPartner: any
  plans: any[]
  selectedPlan: any
  planOptions: { [key: string]: string | number }
  originalPlanOptions: { [key: string]: string | number }
}

class BillingSettingsComponent extends RasaReactComponent<any, BillingSettingsState> {
  public static contextType = RasaContext
  private sharedStore: SharedStore

  constructor(props: any) {
    super(props, '', {
      communityId: '',
      isLoading: false,
      isPartnersLoading: false,
      isPlansLoading: false,
      isSaving: false,
      showConfirmModal: false,
      partners: [],
      selectedPartner: null,
      plans: [],
      selectedPlan: null,
      planOptions: {},
      originalPlanOptions: {}
    })
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    this.sharedStore.getValue(SharedKeys.activeCommunity)
    .then((activeCommunity) => {
      this.setState({
        communityId: activeCommunity.communityId,
      }, () => {
        this.loadPartners()
      })
    })
  }

  public render() {
    return (
      <div className="billing-settings-wrapper">
        <div className="header-container">
          <HeaderComponent
            title={'Billing Settings'}
            subTitle={'Billing Management'}
            description={[
              'Manage billing settings for partners and plans.',
            ]}/>
        </div>
        {this.state.isLoading ? <Loading size="64"/> :
          <div className="bs-form-wrap">
            <div className="bs-form-row">
              <label>Select Partner</label>
              <Select
                options={this.state.partners.map((partner) => ({
                  label: partner.name,
                  value: partner.identifier,
                }))}
                value={this.state.selectedPartner}
                onChange={(e) => this.onPartnerChange(e)}
                placeholder="Select partner..."
                noOptionsMessage={() => this.state.isPartnersLoading ? 'Loading...' : 'No partners found'}
                isLoading={this.state.isPartnersLoading}
                isSearchable={true}
              />
            </div>
            <div className="bs-form-row">
              <label>Select Plan</label>
              {this.state.isPlansLoading ? <Loading size="32"/> :
              <Select
                placeholder="Select plan..."
                options={this.state.plans.map((plan) => {
                  return {
                    label: plan.name,
                    value: plan.id,
                  }
                })}
                value={this.state.selectedPlan}
                onChange={(e) => this.onPlanChange(e)}
                disabled={!this.state.selectedPartner}
                noOptionsMessage={() => this.state.isPlansLoading ? 'Loading...' : 'No plans found'}
              />}
              {this.state.selectedPartner && this.state.plans.length === 0 && !this.state.isPlansLoading && (
                <div className="no-plans-message">
                  No plans available for this partner
                </div>
              )}
            </div>
            {this.renderPlanOptions()}
            {this.renderSaveButton()}
          </div>
        }
        {this.renderConfirmModal()}
      </div>
    )
  }

  private loadPartners = () => {
    if (!this.state.communityId) return

    this.setState({ isPartnersLoading: true })

    new Dataset().loadCommunityDataset('partners', this.state.communityId)
    .then((result) => {
      const partners = result[0]
        .filter((partner) => partner.identifier !== 'rasa')
        .map((partner) => {
        return {
          identifier: partner.identifier,
          name: partner.name,
        }
      })
      this.setState({
        partners,
        isPartnersLoading: false
      })
    })
    .catch(() => {
      this.setState({
        partners: [],
        isPartnersLoading: false
      })
    })
  }

  private onPartnerChange = (selected: any) => {
    const partnerIdentifier = selected.value
    this.setState({
      selectedPartner: selected,
      selectedPlan: null,
      plans: []
    }, () => {
      if (partnerIdentifier) {
        this.loadPlans(partnerIdentifier)
      }
    })
  }

  private onPlanChange = (selected: any) => {
    const selectedPlanData = this.state.plans.find(plan => plan.id === selected.value)
    const options = selectedPlanData && selectedPlanData.options ? selectedPlanData.options : {}
    const planOptions = typeof options === 'string' ? JSON.parse(options) : options
    const finalOptions = planOptions || {}

    // Normalize all values (keep integers as numbers, others as strings)
    const normalizedOptions: { [key: string]: string | number } = {}
    Object.keys(finalOptions).forEach(key => {
      const value = finalOptions[key]
      if (value === null || value === undefined) {
        normalizedOptions[key] = ''
      } else if (Number.isInteger(Number(value)) && !isNaN(Number(value))) {
        normalizedOptions[key] = Number(value)
      } else {
        normalizedOptions[key] = String(value)
      }
    })

    this.setState({
      selectedPlan: selected,
      planOptions: normalizedOptions,
      originalPlanOptions: JSON.parse(JSON.stringify(normalizedOptions))
    })
  }

  private loadPlans = (partnerIdentifier: string) => {
    this.setState({ isPlansLoading: true })

    new Dataset().loadCommunityDataset('billingPlansForPartner', this.state.communityId, [
      { param: 'partnerIdentifier', value: partnerIdentifier }
    ])
    .then((result) => {
      const plans = result[0].map((plan) => {
        return {
          id: plan.id,
          code_annual: plan.code_annual,
          code_monthly: plan.code_monthly,
          name: plan.name,
          options: plan.options,
          plan_code: plan.code_monthly || plan.code_annual,
        }
      })
      this.setState({
        plans,
        isPlansLoading: false
      })
    })
    .catch(() => {
      this.setState({
        plans: [],
        isPlansLoading: false
      })
    })
  }

  private renderPlanOptions = () => {
    if (!this.state.selectedPlan || Object.keys(this.state.planOptions).length === 0) {
      return null
    }

    return (
      <div className="bs-form-row">
        <label>Plan Options</label>
        <div className="plan-options-container">
          {Object.keys(this.state.planOptions).map((optionKey) => (
            <div key={optionKey} className="plan-option-field">
              <label>{this.formatOptionLabel(optionKey)}</label>
              {this.renderOptionInput(optionKey)}
            </div>
          ))}
        </div>
      </div>
    )
  }

  private onOptionChange = (optionKey: string, value: string) => {
    let processedValue: string | number = value

    // If the current value is an integer, try to convert the new value to integer
    const currentValue = this.state.planOptions[optionKey]
    if (typeof currentValue === 'number' && Number.isInteger(currentValue)) {
      const numValue = Number(value)
      if (!isNaN(numValue) && Number.isInteger(numValue)) {
        processedValue = numValue
      } else if (value === '' || value === '-') {
        // Allow empty string or minus sign for typing
        processedValue = value
      } else {
        // Keep as string if not a valid integer
        processedValue = value
      }
    }

    this.setState({
      planOptions: {
        ...this.state.planOptions,
        [optionKey]: processedValue
      }
    })
  }

  private formatOptionLabel = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  private isIntegerValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') {
      return false
    }
    const num = Number(value)
    return Number.isInteger(num) && !isNaN(num)
  }

  private renderOptionInput = (optionKey: string) => {
    const value = this.state.planOptions[optionKey] || ''
    const isInteger = this.isIntegerValue(value)

    if (isInteger) {
      return (
        <input
          type="number"
          value={value}
          placeholder={`Enter ${this.formatOptionLabel(optionKey)}...`}
          onChange={(e) => this.onOptionChange(optionKey, e.target.value)}
        />
      )
    }

    return (
      <input
        type="text"
        value={value}
        placeholder={`Enter ${this.formatOptionLabel(optionKey)}...`}
        onChange={(e) => this.onOptionChange(optionKey, e.target.value)}
      />
    )
  }

  private renderSaveButton = () => {
    if (!this.state.selectedPlan || Object.keys(this.state.planOptions).length === 0) {
      return null
    }

    const hasChanges = this.hasOptionsChanged()

    return (
      <div className="bs-form-row bs-btn-wrap">
        <span className="buttons">
          <Button
            onClick={() => this.setState({ showConfirmModal: true })}
            disabled={this.state.isSaving || !this.state.selectedPartner || !this.state.selectedPlan || !hasChanges}
          >
            {this.state.isSaving ? 'Saving...' : 'Save'}
          </Button>
        </span>
      </div>
    )
  }

  private hasOptionsChanged = (): boolean => {
    const currentOptions = this.state.planOptions
    const originalOptions = this.state.originalPlanOptions

    // Convert both to JSON strings for deep comparison
    const currentJson = JSON.stringify(currentOptions)
    const originalJson = JSON.stringify(originalOptions)

    return currentJson !== originalJson
  }

  private saveBillingPlan = () => {
    this.setState({ isSaving: true, showConfirmModal: false })

    this.context.entityMetadata
      .getEntityObject('billing_plan')
      .then((entity) => {
        entity.setRecordId(this.state.communityId, this.state.selectedPlan.value)
        entity.data.options = this.state.planOptions

        return entity.save()
      })
      .then(() => {
        this.context.store.dispatch(Flash.showFlashMessage('Plan options saved successfully!'))
        this.setState({
          isSaving: false,
          originalPlanOptions: JSON.parse(JSON.stringify(this.state.planOptions))
        })
      })
      .catch(() => {
        this.context.store.dispatch(Flash.showFlashError('Failed to save plan options. Please try again.'))
        this.setState({ isSaving: false })
      })
  }

  private onModalClose = () => {
    this.setState({ showConfirmModal: false })
  }

  private renderConfirmModal = () => {
    return (
      <Modal isOpen={this.state.showConfirmModal}>
        <ModalBody className="delete-modal">
          <div>
            <h4>Confirm Save</h4>
            <p>
              Are you sure you want to save the billing plan for{' '}
              <strong>{this.state.selectedPlan ? this.state.selectedPlan.label : 'this plan'}</strong>?
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.saveBillingPlan} disabled={this.state.isSaving}>
            {this.state.isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button color="secondary" onClick={this.onModalClose} disabled={this.state.isSaving}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    )
  }
}

export const BillingSettings = GenericRedux.registerNewComponent<any>(
  BillingSettingsComponent,
  'billing_settings',
  {})